import type { CSSProperties, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

export type MobileEdgeSwipeBackOptions = {
  isMobile: boolean;
  /** false のときリスナーを付けない（下にパネルがある・モーダル表示中など） */
  enabled: boolean;
  onNavigateBack: () => void;
  /** 左端からこの px 以内で開始したタッチのみ（戻りボタンと競合しないよう狭め） */
  edgeWidthPx?: number;
  /** 指を離したとき、画面幅に対するこの割合以上右に動かしていれば戻る */
  thresholdRatio?: number;
};

/**
 * スマホ：画面左端ベゼル付近から右方向にスワイプして「＜」と同じ戻りを実行。
 * iOS のインタラクティブ・ポップに近い追従（translateX）を返す。
 */
export function useMobileEdgeSwipeBack(
  containerRef: RefObject<HTMLElement | null>,
  {
    isMobile,
    enabled,
    onNavigateBack,
    edgeWidthPx = 14,
    thresholdRatio = 0.3,
  }: MobileEdgeSwipeBackOptions
): { swipeStyle: CSSProperties | undefined; isSwipeDragging: boolean } {
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const [isSwipeDragging, setIsSwipeDragging] = useState(false);
  const dragOffsetRef = useRef(0);
  const onNavigateBackRef = useRef(onNavigateBack);
  onNavigateBackRef.current = onNavigateBack;

  useEffect(() => {
    const el = containerRef.current;
    if (!isMobile || !enabled || !el) return;

    let active = false;
    let startX = 0;
    let startY = 0;
    let width = typeof window !== 'undefined' ? window.innerWidth : 400;

    const cleanupDoc = () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchCancel);
    };

    const finish = (fireBack: boolean) => {
      if (!active) return;
      active = false;
      setIsSwipeDragging(false);
      cleanupDoc();
      const x = dragOffsetRef.current;
      const w = width;
      if (fireBack && x > w * thresholdRatio) {
        onNavigateBackRef.current();
      }
      dragOffsetRef.current = 0;
      setDragOffsetPx(0);
    };

    const onTouchEnd = () => finish(true);

    const onTouchCancel = () => finish(false);

    const onTouchMove = (e: TouchEvent) => {
      if (!active) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (dx < -10) {
        finish(false);
        return;
      }
      if (Math.abs(dy) > Math.abs(dx) * 1.15 && Math.abs(dy) > 20) {
        finish(false);
        return;
      }
      if (dx > 6) {
        e.preventDefault();
      }
      const next = Math.min(Math.max(0, dx), width);
      dragOffsetRef.current = next;
      setDragOffsetPx(next);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (active) return;
      const t = e.touches[0];
      if (!t || t.clientX > edgeWidthPx) return;
      if (!el.contains(e.target as Node)) return;
      // iOS Safari の左端スワイプ「戻る」を、自前ジェスチャで上書きするため touchstart で握る
      e.preventDefault();
      active = true;
      setIsSwipeDragging(true);
      startX = t.clientX;
      startY = t.clientY;
      width = window.innerWidth;
      dragOffsetRef.current = 0;
      setDragOffsetPx(0);
      document.addEventListener('touchmove', onTouchMove, { passive: false });
      document.addEventListener('touchend', onTouchEnd, { passive: true });
      document.addEventListener('touchcancel', onTouchCancel, { passive: true });
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false, capture: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart, { capture: true });
      if (active) finish(false);
    };
  }, [isMobile, enabled, containerRef, edgeWidthPx, thresholdRatio]);

  if (!isMobile) {
    return { swipeStyle: undefined, isSwipeDragging: false };
  }

  return {
    swipeStyle: {
      transform: `translate3d(${dragOffsetPx}px,0,0)`,
      transition: isSwipeDragging ? 'none' : 'transform 0.22s cubic-bezier(0.25, 0.1, 0.25, 1)',
    },
    isSwipeDragging,
  };
}
